<template>
    <v-dialog v-model="dialog" max-width="500" @click:outside="hide">
        <v-card class="card-container">
            <v-card-title class="title-container">
                <v-icon color="#FFFFFF" @click="hide" large class="mr-3">mdi-close-circle-outline</v-icon>
                {{ $t('programs.save_in') }}
            </v-card-title>
            <v-card-text class="mt-3">
                <v-row dense>
                    <v-col cols="12">
                        <kimsa-text-field :label="$t('common.name')" :placeholder="$t('programs.name_placeholder')"
                            outlined dense force-value :value="name" @change="name = $event" no-elevation />
                    </v-col>
                    <v-col cols="12">
                        <kimsa-combobox :label="$t('common.category')" :placeholder="$t('exercises.pick_one_category')"
                            outlined dense :items="categories" @change="category = $event" ref="categories" />
                    </v-col>
                    <v-col cols="6">
                        <kimsa-date-picker :label="$t('routines.from')" solo flat dense outlined color="secondary"
                            :value="startDate" ref="start" disabled />
                    </v-col>
                    <v-col cols="6">
                        <kimsa-date-picker :disabled="!startDate" :label="$t('routines.to')" solo flat dense outlined
                            color="secondary" :min="startDate" @change="endDate = $event" :value="endDate" ref="end" />
                    </v-col>
                    <v-col cols="12">
                        <kimsa-textarea-field :label="$t('common.description')"
                            :placeholder="$t('programs.name_placeholder')" outlined force-value :value="description"
                            @change="description = $event" no-elevation />
                    </v-col>
                    <v-col cols="12">
                        <kimsa-tags-picker ref="tagsPicker" @change="tags = $event" :default-tags="tags"
                            :label="$t('exercises.tags')" force-value />
                    </v-col>
                    <v-col cols="12">
                        <kimsa-upload-file :label="$t('common.image')" relative-path="images/routines"
                            mime-types="image/*" @change="file = $event" :preview-img="previewImg" ref="img"
                            height="150" />
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="px-0 pb-0">
                <v-btn block depressed x-large color="primary" class="normal-btn no-radius black--text"
                    :disabled="disabled" @click="save" :loading="loading">
                    {{ $t('common.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import { db, Timestamp } from "@/utils/Firebase";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaUploadFile from "@/components/common/inputs/KimsaUploadFile";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaDatePicker from "@/components/common/inputs/KimsaDatePicker";
import { clearBlocksAssignedRoutine, getPayloadBaseProgram } from "@/utils/Exercises";
import KimsaTextareaField from "@/components/common/inputs/KimsaTextareaField";
import KimsaTagsPicker from '../../common/inputs/KimsaTagsPicker.vue';

export default {
    name: "SaveProgramDialog",
    components: { KimsaTextareaField, KimsaDatePicker, KimsaCombobox, KimsaUploadFile, KimsaTextField, KimsaTagsPicker },
    props: {
        currentSelection: {
            type: [Object, String],
            default: null,
        },
        coachId: {
            type: String,
            default: null
        }
    },
    data(vm) {
        return {
            dialog: false,
            loading: false,
            name: '',
            category: null,
            endDate: null,
            startDate: null,
            description: '',
            file: null,
            tags: []
        }
    },
    computed: {
        ...mapState(['dialogActive', 'categoriesOptions', "loadingCategories"]),
        disabled(vm = this) {
            return !vm.name || !vm.category || !vm.startDate || !vm.endDate;
        },
        previewImg(vm = this) {
            return vm.file?.img || null
        },
        collection(vm = this) {
            return vm.currentSelection?.objectID ? 'assignedRoutines' : 'groupAssignedRoutines'
        },
        categories(vm = this) {
            return [...vm.categoriesOptions, { id: 1, name: vm.$t('categories.mix'), value: vm.$t('categories.mix') }]
        }
    },
    async mounted() {
        const vm = this
        // console.log(vm)
        if (!vm.categoriesOptions || vm.categoriesOptions.length === 0) await vm.fetchExerciseCategories()
    },
    methods: {
        ...mapMutations(['setDialogActive']),
        ...mapActions(['fetchExerciseCategories']),
        show(date) {
            const vm = this
            vm.startDate = date
            vm.dialog = true
            vm.setDialogActive(true)
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.setDialogActive(false)
            vm.reset()
        },
        reset() {
            const vm = this
            vm.name = ''
            if (vm.$refs.categories) vm.$refs.categories.model = null
            if (vm.$refs.img) vm.$refs.img.reset()
            vm.category = null
            vm.startDate = null
            vm.endDate = null
            vm.description = ''
            vm.file = null
            vm.tags = []
        },
        async loadRoutines() {
            const vm = this

            if (vm.collection === 'assignedRoutines') {
                return await db.collection(vm.collection)
                    .where('studentId', '==', vm.currentSelection.objectID)
                    .where('rawDate.full', ">=", vm.startDate)
                    .where('rawDate.full', "<=", vm.endDate)
                    .orderBy('rawDate.full', 'asc').get()
            } else {
                return await db.collection(vm.collection)
                    .where('groupId', '==', vm.currentSelection.id)
                    .where('teamId', '==', vm.currentSelection?.teamId || null)
                    .where('rawDate.full', ">=", vm.startDate)
                    .where('rawDate.full', "<=", vm.endDate)
                    .orderBy('rawDate.full', 'asc').get()
            }
        },
        async save() {
            const vm = this
            vm.loading = true

            let routines = await vm.loadRoutines()

            let assignedRoutines = []
            for (let routine of routines.docs) {
                assignedRoutines.push({ blocks: clearBlocksAssignedRoutine(routine.data().blocks) })
            }

            let img = null
            if (vm.file) img = await vm.$refs.img.upload()

            let payloadProgram = getPayloadBaseProgram(vm.name, vm.coachId, assignedRoutines, vm.category?.name || '', vm.description, img, vm.tags)

            await db.collection('programs').add(payloadProgram)

            vm.loading = false
            vm.hide()
            vm.toast(vm.$t('programs.saved'))
        },
    },
    watch: {
        dialogActive(val) {
            if (!val) this.hide()
        },
    }
}
</script>

<style scoped>
.card-container {
    border-radius: 8px;
    overflow: hidden;
}

.title-container {
    color: #FFFFFF;
    background: var(--v-backGroundPrimary-base);
}
</style>